import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// DayPilot imports removed - using custom Gantt Chart implementation
import { fetchgastenverbruik } from "../Reducers/actions";
import moment from "moment";
import "moment/locale/nl";

moment.locale('nl');

const PlanningOverzicht = () => {
  const dispatch = useDispatch();
  const gastenverbruik = useSelector((state) => state.gastenverbruik.gastenverbruik);
  const user = useSelector((state) => state.auth.user);

  const [loading, setLoading] = useState(false);
  const [ganttTasks, setGanttTasks] = useState([]);
  const [ganttResources, setGanttResources] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));


  useEffect(() => {
    loadPlanningData();
  }, [dispatch, selectedDate]);

  const loadPlanningData = async () => {
    setLoading(true);
    try {
      const startOfPeriod = moment(selectedDate).subtract(2, 'weeks');
      const endOfPeriod = moment(selectedDate).add(2, 'weeks');

      const query = {
        aankomstVanaf: startOfPeriod.format('YYYY-MM-DD'),
        vertrekTot: endOfPeriod.format('YYYY-MM-DD')
      };

      await dispatch(fetchgastenverbruik(query));
    } catch (error) {
      console.error("Error loading planning data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gastenverbruik && gastenverbruik.length > 0) {
      convertToGanttData();
    }
  }, [gastenverbruik]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'actief':
        return '#28a745'; // Green
      case 'confirmed':
        return '#17a2b8'; // Blue
      case 'pending':
        return '#ffc107'; // Yellow
      case 'canceled':
      case 'vertrokken':
        return '#6c757d'; // Gray
      default:
        return '#007bff'; // Default blue
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
      case 'actief':
        return 'badge-success';
      case 'confirmed':
        return 'badge-info';
      case 'pending':
        return 'badge-warning';
      case 'canceled':
      case 'vertrokken':
        return 'badge-secondary';
      default:
        return 'badge-light';
    }
  };


  const convertToGanttData = () => {
    // Create unique resources based on locations
    const uniqueLocations = [...new Set(gastenverbruik.map(reservation =>
      reservation.standplaats?.naam || reservation.accommodaties?.naam || 'Onbekende locatie'
    ))];

    const resources = uniqueLocations.map((location, index) => ({
      id: `location_${index}`,
      name: location,
      expanded: true
    }));

    // Create tasks for each reservation
    const tasks = gastenverbruik.map((reservation) => {
      const guestName = reservation.gast?.voornaam && reservation.gast?.achternaam
        ? `${reservation.gast.voornaam} ${reservation.gast.achternaam}`
        : 'Onbekende gast';

      const location = reservation.standplaats?.naam || reservation.accommodaties?.naam || 'Onbekende locatie';
      const locationIndex = uniqueLocations.indexOf(location);

      return {
        id: reservation._id,
        resource: `location_${locationIndex}`,
        start: moment(reservation.aankomst).format('YYYY-MM-DD'),
        end: moment(reservation.vertrek).format('YYYY-MM-DD'),
        text: `${guestName} (${reservation.personen || 0} pers.)`,
        backColor: getStatusColor(reservation.status),
        borderColor: getStatusColor(reservation.status),
        fontColor: "#ffffff",
        data: {
          id: reservation._id,
          gast: guestName,
          location: location,
          status: reservation.status,
          telefoon: reservation.gast?.telefoon || '',
          email: reservation.gast?.email || '',
          aankomst: reservation.aankomst,
          vertrek: reservation.vertrek,
          personen: reservation.personen || 0
        }
      };
    });

    setGanttResources(resources);
    setGanttTasks(tasks);
  };

  const renderGanttChart = () => {
    const startDate = moment(selectedDate).startOf('week').startOf('day');
    const endDate = moment(selectedDate).endOf('week').endOf('day');
    const days = [];
    let current = startDate.clone();

    // Generate array of days for the timeline (one week)
    while (current.isSameOrBefore(endDate)) {
      days.push(current.clone());
      current.add(1, 'day');
    }

    // Group reservations by location
    const locationGroups = {};
    ganttTasks.forEach(task => {
      const location = task.data.location;
      if (!locationGroups[location]) {
        locationGroups[location] = [];
      }
      locationGroups[location].push(task);
    });

    const dayWidth = Math.max(100, 800 / days.length); // Minimum 100px per day for weekly view

    return (
      <div className="custom-gantt">
        {/* Header with dates */}
        <div className="gantt-header" style={{ display: 'flex', borderBottom: '2px solid #dee2e6' }}>
          <div style={{ width: '250px', padding: '10px', background: '#f8f9fa', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>
            Locatie / Gast
          </div>
          <div style={{ display: 'flex', flex: 1, minWidth: `${days.length * dayWidth}px` }}>
            {days.map((day, index) => (
              <div
                key={index}
                style={{
                  width: `${dayWidth}px`,
                  minWidth: `${dayWidth}px`,
                  padding: '5px',
                  textAlign: 'center',
                  borderRight: '1px solid #dee2e6',
                  background: day.isSame(moment(), 'day') ? '#e3f2fd' : '#f8f9fa',
                  fontSize: '12px',
                  fontWeight: day.day() === 0 || day.day() === 6 ? 'bold' : 'normal'
                }}
              >
                <div>{day.format('DD')}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>{day.format('ddd')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Rows for each location */}
        <div className="gantt-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {Object.entries(locationGroups).map(([location, reservations], locationIndex) => (
            <div key={locationIndex} style={{ display: 'flex', borderBottom: '1px solid #dee2e6', height: '80px' }}>
              {/* Location name column */}
              <div style={{
                width: '250px',
                padding: '15px 12px',
                fontWeight: 'bold',
                borderRight: '1px solid #dee2e6',
                background: '#f8f9fa',
                display: 'flex',
                alignItems: 'center'
              }}>
                📍 {location}
              </div>

              {/* Timeline area for this location */}
              <div style={{
                display: 'flex',
                flex: 1,
                minWidth: `${days.length * dayWidth}px`,
                position: 'relative',
                height: '80px'
              }}>
                {/* Timeline background grid */}
                {days.map((day, dayIndex) => (
                  <div
                    key={dayIndex}
                    style={{
                      width: `${dayWidth}px`,
                      minWidth: `${dayWidth}px`,
                      height: '100%',
                      borderRight: '1px solid #eee',
                      background: day.day() === 0 || day.day() === 6 ? '#f8f9fa' : 'white'
                    }}
                  />
                ))}

                {/* All reservation bars for this location with proper row assignment */}
                {(() => {
                  // Sort reservations by start date
                  const sortedReservations = reservations.sort((a, b) => moment(a.start).diff(moment(b.start)));

                  // Calculate row assignments to prevent overlaps
                  const rowAssignments = [];
                  const occupiedRows = []; // Track what time periods each row covers

                  sortedReservations.forEach((reservation, index) => {
                    const resStart = moment(reservation.start);
                    const resEnd = moment(reservation.end);

                    // Find the first available row for this reservation
                    let assignedRow = 0;
                    let foundRow = false;

                    while (!foundRow) {
                      // Check if this row is free during our time period
                      const rowOccupied = occupiedRows[assignedRow] || [];
                      let hasConflict = false;

                      for (let occupied of rowOccupied) {
                        // Check for time overlap: reservation overlaps if it starts before occupied ends AND ends after occupied starts
                        if (resStart.isBefore(occupied.end) && resEnd.isAfter(occupied.start)) {
                          hasConflict = true;
                          break;
                        }
                      }

                      if (!hasConflict) {
                        // This row is free, assign it
                        if (!occupiedRows[assignedRow]) {
                          occupiedRows[assignedRow] = [];
                        }
                        occupiedRows[assignedRow].push({
                          start: resStart.clone(),
                          end: resEnd.clone(),
                          reservation: reservation
                        });
                        rowAssignments.push({ reservation, row: assignedRow });
                        foundRow = true;
                      } else {
                        // Try next row
                        assignedRow++;
                      }
                    }
                  });

                  // Now render all reservations with their assigned rows
                  return rowAssignments.map(({ reservation, row }, index) => {
                    const resStart = moment(reservation.start);
                    const resEnd = moment(reservation.end);
                    const startDayIndex = Math.max(0, resStart.diff(startDate, 'days'));
                    const duration = Math.min(resEnd.diff(resStart, 'days'), days.length - startDayIndex);

                    if (duration > 0 && startDayIndex < days.length) {
                      const barHeight = 22;
                      const barMargin = 3;
                      const topOffset = 8 + row * (barHeight + barMargin);

                      return (
                        <div
                          key={index}
                          onClick={() => window.location.href = `/gastenverbruik/kampeerplaats/${reservation.data.id}`}
                          style={{
                            position: 'absolute',
                            left: `${startDayIndex * dayWidth + 2}px`,
                            top: `${topOffset}px`,
                            width: `${duration * dayWidth - 4}px`,
                            height: `${barHeight}px`,
                            background: reservation.backColor,
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            border: `2px solid ${reservation.borderColor}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            padding: '0 6px',
                            zIndex: 10 + index
                          }}
                          title={`${reservation.data.gast} (${reservation.data.personen} pers.) - ${resStart.format('DD/MM')} tot ${resEnd.format('DD/MM')}`}
                        >
                          {duration >= 4 ?
                            `${reservation.data.gast} (${resStart.format('DD/MM')})` :
                            duration >= 2 ?
                              `${reservation.data.gast}` :
                              `${reservation.data.gast.split(' ')[0]}`
                          }
                        </div>
                      );
                    }
                    return null;
                  });
                })()}

                {/* Show message if no reservations */}
                {reservations.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#999',
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}>
                    Geen reserveringen deze maand
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Legend for Gantt view */}
        <div className="gantt-legend mt-3" style={{ padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
          <h6>Gantt Chart Legenda:</h6>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '20px', height: '15px', background: '#28a745', marginRight: '5px', borderRadius: '3px' }}></div>
              <span style={{ fontSize: '12px' }}>Actieve Reservering</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '20px', height: '15px', background: '#17a2b8', marginRight: '5px', borderRadius: '3px' }}></div>
              <span style={{ fontSize: '12px' }}>Bevestigde Reservering</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '20px', height: '15px', background: '#ffc107', marginRight: '5px', borderRadius: '3px' }}></div>
              <span style={{ fontSize: '12px' }}>In Behandeling</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '20px', height: '15px', background: '#6c757d', marginRight: '5px', borderRadius: '3px' }}></div>
              <span style={{ fontSize: '12px' }}>Geannuleerd/Vertrokken</span>
            </div>
          </div>
          <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
            💡 Tip: Klik op een reservering balk om de details te bewerken. Weekend dagen zijn gemarkeerd in grijs.
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="content-header">
            <h1 className="content-header-title">
              <i className="fas fa-chart-bar mr-3"></i>
              Planning Gantt Chart
            </h1>
            <div className="content-header-subtitle">
              Gantt Chart weergave van alle reserveringen en gastenverbruik per locatie
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Totaal Reserveringen
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {ganttTasks.length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-calendar fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Actieve Reserveringen
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {ganttTasks.filter(t => t.data && (t.data.status === 'active' || t.data.status === 'actief')).length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    In Behandeling
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {ganttTasks.filter(t => t.data && t.data.status === 'pending').length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-clock fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Bevestigd
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {ganttTasks.filter(t => t.data && t.data.status === 'confirmed').length}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-thumbs-up fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title">Legenda</h6>
              <div className="d-flex flex-wrap gap-3">
                <div className="d-flex align-items-center me-3">
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#28a745', marginRight: '8px' }}></div>
                  <span>Actief</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#17a2b8', marginRight: '8px' }}></div>
                  <span>Bevestigd</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#ffc107', marginRight: '8px' }}></div>
                  <span>In behandeling</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#6c757d', marginRight: '8px' }}></div>
                  <span>Geannuleerd/Vertrokken</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Date Navigation Controls */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setSelectedDate(moment(selectedDate).subtract(1, 'week').format('YYYY-MM-DD'))}
                  >
                    <i className="fas fa-chevron-left"></i> Vorige Week
                  </button>

                  <input
                    type="week"
                    className="form-control"
                    style={{ width: '200px' }}
                    value={moment(selectedDate).format('YYYY-[W]WW')}
                    onChange={(e) => {
                      const year = e.target.value.split('-W')[0];
                      const week = e.target.value.split('-W')[1];
                      setSelectedDate(moment().year(year).week(week).startOf('week').format('YYYY-MM-DD'));
                    }}
                  />

                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setSelectedDate(moment(selectedDate).add(1, 'week').format('YYYY-MM-DD'))}
                  >
                    Volgende Week <i className="fas fa-chevron-right"></i>
                  </button>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setSelectedDate(moment().format('YYYY-MM-DD'))}
                  >
                    <i className="fas fa-calendar-day"></i> Vandaag
                  </button>

                  <div className="text-muted">
                    <strong>Week {moment(selectedDate).format('WW')} - {moment(selectedDate).format('MMMM YYYY')}</strong>
                    <br />
                    <small>{moment(selectedDate).startOf('week').format('DD MMM')} - {moment(selectedDate).endOf('week').format('DD MMM YYYY')}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-body">
              <div className="gantt-view" style={{ overflowX: 'auto' }}>
                {renderGanttChart()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningOverzicht;